import {Button} from "primereact/button";
import {Calendar} from "primereact/calendar";
import {Checkbox} from "primereact/checkbox";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {RadioButton} from "primereact/radiobutton";
import {SelectButton} from "primereact/selectbutton";
import React, {useRef, useState} from "react";
import {useTranslation} from "react-i18next";

import {format, parseISO} from "date-fns";

import {useRevaTranslation} from "../../hooks/languageHooks";
import {RowDeleteIcon} from "../paramInput/RowDeleteIcon";
import {
    type Certificate,
    type DocumentData,
    EMPTY_CERT,
    isCertificateDateMissing,
    isValidUploadFileName,
    type OwnerData,
    type OwnerType,
    PARCEL_OPTIONS,
    type RegisterData,
    type UnitNumbers,
} from "./commissionOrderUtil";

interface DocPanelProps<T> {
    info: T;
    onChange: <F extends keyof T>(field: F, value: T[F]) => void;
    disabled: boolean;
}

interface CertificateListProps {
    idPrefix: string;
    certificates: Certificate[];
    onChange: (certificates: Certificate[]) => void;
    disabled: boolean;
    className?: string;
    showInvalid: boolean;
}

function CertificateList({idPrefix, certificates, onChange, disabled, className, showInvalid}: CertificateListProps) {
    const {t, language} = useRevaTranslation();

    const updateCert = (idx: number, field: keyof Certificate, value: string | null) => {
        const next = certificates.map((c, i) => (i === idx ? {...c, [field]: value} : c));
        const last = next[next.length - 1];
        onChange(last.number || last.date ? [...next, EMPTY_CERT()] : next);
    };
    const removeCert = (idx: number) => onChange(certificates.filter((_, i) => i !== idx));

    return (
        <div className={`grid grid-cols-[1rem_1fr_1fr] gap-x-4 gap-y-2 ${className ?? ""}`}>
            <div />
            <label id={`${idPrefix}-certificate-number-label`}>
                {t("commissionOrderDialog.documents.certificateNumber")}
            </label>
            <label id={`${idPrefix}-certificate-date-label`}>
                {t("commissionOrderDialog.documents.certificateDate")}
                <span className="commission-required">*</span>
            </label>
            {certificates.map((cert, idx) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity of their own, values are fully controlled from `certificates`
                <React.Fragment key={idx}>
                    <div className="flex items-center">
                        {/* Nur bestehende Zeilen bekommen das klickbare Minus; die leere Neu-Zeile bleibt ohne Icon. */}
                        {idx !== certificates.length - 1 && (
                            <RowDeleteIcon onDelete={() => removeCert(idx)} disabled={disabled} />
                        )}
                    </div>
                    <InputText
                        aria-labelledby={`${idPrefix}-certificate-number-label`}
                        className="w-full p-inputtext-sm"
                        value={cert.number ?? ""}
                        maxLength={50}
                        onChange={(e) => updateCert(idx, "number", e.target.value)}
                        disabled={disabled}
                    />
                    <Calendar
                        ariaLabelledBy={`${idPrefix}-certificate-date-label`}
                        className="w-full"
                        inputClassName={`p-inputtext-sm${showInvalid && isCertificateDateMissing(certificates, idx) ? " p-invalid" : ""}`}
                        value={cert.date ? parseISO(cert.date) : null}
                        onChange={(e) =>
                            updateCert(idx, "date", e.target.value ? format(e.target.value, "yyyy-MM-dd") : null)
                        }
                        disabled={disabled}
                        showIcon
                        showButtonBar
                        dateFormat="dd.mm.yy"
                        locale={language}
                        pt={{dropdownButton: {root: {className: "p-button-sm"}}}}
                    />
                </React.Fragment>
            ))}
        </div>
    );
}

function UnitNumberFields({
    idPrefix,
    className,
    info,
    onChange,
    disabled,
}: {
    idPrefix: string;
    className: string;
    info: UnitNumbers;
    onChange: (field: keyof UnitNumbers, value: string) => void;
    disabled: boolean;
}) {
    const {t} = useTranslation();
    return (
        <div className={className}>
            {(["apartmentNumber", "basementNumber", "parkingNumber"] as const).map((name) => (
                <div key={name} className="field col-span-4 !mb-0">
                    <label htmlFor={`${idPrefix}-${name}`}>{t(`commissionOrderDialog.documents.${name}`)}</label>
                    <InputText
                        id={`${idPrefix}-${name}`}
                        className="w-full p-inputtext-sm"
                        value={info[name] ?? ""}
                        onChange={(e) => onChange(name, e.target.value)}
                        disabled={disabled}
                    />
                </div>
            ))}
        </div>
    );
}

function DeclarationOfCondominiumPanel({
    info,
    onChange,
    disabled,
    showInvalid,
}: DocPanelProps<DocumentData["declarationOfCondominium"]> & {showInvalid: boolean}) {
    const {t} = useTranslation();

    return (
        <div className="commission-panel">
            <span className="font-bold">{t("commissionOrderDialog.documents.declarationOfCondominium")}</span>
            <CertificateList
                idPrefix="doc-condominium"
                className="mt-4"
                certificates={info.certificates}
                onChange={(certs) => onChange("certificates", certs)}
                disabled={disabled}
                showInvalid={showInvalid}
            />
            <UnitNumberFields
                idPrefix="doc-condominium"
                className="formgrid grid grid-cols-12 gap-4 !mt-4"
                info={info}
                onChange={onChange}
                disabled={disabled}
            />
        </div>
    );
}

function RegistrationApprovalPanel({
    info,
    onChange,
    disabled,
    showInvalid,
}: DocPanelProps<DocumentData["registrationApproval"]> & {showInvalid: boolean}) {
    const {t} = useTranslation();
    const descriptionInvalid = showInvalid && !info.description;

    return (
        <div className="commission-panel">
            <span className="font-bold">{t("commissionOrderDialog.documents.registrationApproval")}</span>
            <div className="field mt-4">
                <label htmlFor="doc-law-desc">
                    {t("commissionOrderDialog.documents.law")}
                    <span className="commission-required">*</span>
                </label>
                <InputText
                    id="doc-law-desc"
                    className={`w-full p-inputtext-sm${descriptionInvalid ? " p-invalid" : ""}`}
                    value={info.description ?? ""}
                    onChange={(e) => onChange("description", e.target.value)}
                    disabled={disabled}
                />
            </div>
            <CertificateList
                idPrefix="doc-registration"
                certificates={info.certificates}
                onChange={(certs) => onChange("certificates", certs)}
                disabled={disabled}
                showInvalid={showInvalid}
            />
        </div>
    );
}

function PartitionPlanPanel({info, onChange, disabled}: DocPanelProps<UnitNumbers>) {
    const {t} = useTranslation();
    return (
        <div className="commission-panel">
            <span className="font-bold mb-3 block">{t("commissionOrderDialog.documents.partitionPlan")}</span>
            <UnitNumberFields
                idPrefix="doc-partition"
                className="formgrid commission-grid mt-4"
                info={info}
                onChange={onChange}
                disabled={disabled}
            />
        </div>
    );
}

function ParcelPanel({
    info,
    onChange,
    disabled,
    showInvalid,
}: DocPanelProps<DocumentData["parcelInfo"]> & {showInvalid: boolean}) {
    const {t} = useTranslation();
    const entriesInvalid = showInvalid && !info.entries.some((e) => !!e);

    const updateEntry = (idx: number, value: string) => {
        const next = info.entries.map((e, i) => (i === idx ? value : e));
        const last = next[next.length - 1];
        onChange("entries", last ? [...next, null] : next);
    };
    const removeEntry = (idx: number) =>
        onChange(
            "entries",
            info.entries.filter((_, i) => i !== idx),
        );

    return (
        <div className="commission-panel">
            <span className="font-bold">{t("commissionOrderDialog.documents.parcel")}</span>
            <div className="field mt-4">
                <label htmlFor="doc-parcel-flur">{t("commissionOrderDialog.documents.flur")}</label>
                <InputText
                    id="doc-parcel-flur"
                    className="w-full p-inputtext-sm"
                    value={info.parcelNumber ?? ""}
                    onChange={(e) => onChange("parcelNumber", e.target.value)}
                    disabled={disabled}
                />
            </div>
            <div className="grid grid-cols-[1rem_1fr] gap-x-2 gap-y-2 mt-3">
                <div />
                <label id="commission-parcel-number-label">
                    {t("commissionOrderDialog.documents.parcelNumber")}
                    <span className="commission-required">*</span>
                </label>
                {info.entries.map((entry, idx) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity of their own, values are fully controlled from `entries`
                    <React.Fragment key={idx}>
                        <div className="flex items-center">
                            {/* Nur bestehende Zeilen bekommen das klickbare Minus; die leere Neu-Zeile bleibt ohne Icon. */}
                            {idx !== info.entries.length - 1 && (
                                <RowDeleteIcon onDelete={() => removeEntry(idx)} disabled={disabled} />
                            )}
                        </div>
                        <InputText
                            aria-labelledby="commission-parcel-number-label"
                            className={`w-full p-inputtext-sm${entriesInvalid ? " p-invalid" : ""}`}
                            value={entry ?? ""}
                            onChange={(e) => updateEntry(idx, e.target.value)}
                            disabled={disabled}
                        />
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

interface DocumentTypePanelsProps {
    isDocumentCollection: boolean;
    documentOptions: string[];
    documentData: DocumentData;
    onChange: <K extends keyof DocumentData, F extends keyof DocumentData[K]>(
        key: K,
        field: F,
        value: DocumentData[K][F],
    ) => void;
    disabled: boolean;
    showInvalid: boolean;
}

export function DocumentTypePanels({
    isDocumentCollection,
    documentOptions,
    documentData,
    onChange,
    disabled,
    showInvalid,
}: DocumentTypePanelsProps) {
    if (!isDocumentCollection) return null;

    const docPanelProps = <K extends keyof DocumentData>(key: K): DocPanelProps<DocumentData[K]> => ({
        info: documentData[key],
        onChange: (field, value) => onChange(key, field, value),
        disabled,
    });
    return (
        <>
            {documentOptions.includes("declarationOfCondominium") && (
                <DeclarationOfCondominiumPanel
                    {...docPanelProps("declarationOfCondominium")}
                    showInvalid={showInvalid}
                />
            )}
            {documentOptions.includes("partitionPlan") && <PartitionPlanPanel {...docPanelProps("partitionPlan")} />}
            {documentOptions.includes("registrationApproval") && (
                <RegistrationApprovalPanel {...docPanelProps("registrationApproval")} showInvalid={showInvalid} />
            )}
            {PARCEL_OPTIONS.some((o) => documentOptions.includes(o)) && (
                <ParcelPanel {...docPanelProps("parcelInfo")} showInvalid={showInvalid} />
            )}
        </>
    );
}

const MAX_UPLOAD_FILE_SIZE = 4 * 1024 * 1024;

interface FileUploadFieldProps {
    creditorRegistered: boolean;
    file: File | null;
    onFileChange: (file: File | null) => void;
    disabled: boolean;
    showInvalid: boolean;
}

function FileUploadField({creditorRegistered, file, onFileChange, disabled, showInvalid}: FileUploadFieldProps) {
    const {t} = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [rejection, setRejection] = useState<"documentTooLarge" | "documentNameInvalid" | null>(null);
    const invalid = showInvalid && !file;

    const label = creditorRegistered
        ? t("commissionOrderDialog.landRegister.document")
        : t("commissionOrderDialog.owner.document");
    const selectLabel = creditorRegistered
        ? t("commissionOrderDialog.landRegister.documentSelect")
        : t("commissionOrderDialog.owner.documentSelect");
    const hint = creditorRegistered ? (
        <div className="mb-2">{t("commissionOrderDialog.landRegister.documentHint")}</div>
    ) : (
        <div className="mb-2">
            {t("commissionOrderDialog.owner.documentHint")}{" "}
            <a
                href="https://partnerhub.value.ag/Content/Downloads/Formular_HS_Vollmacht_Grundbucheinsicht.pdf"
                target="_blank"
                rel="noreferrer"
            >
                {t("commissionOrderDialog.owner.documentDownload")}
            </a>
        </div>
    );

    const chooseFile = (selected: File | null) => {
        const invalidReason =
            selected && selected.size > MAX_UPLOAD_FILE_SIZE
                ? "documentTooLarge"
                : selected && !isValidUploadFileName(selected.name)
                  ? "documentNameInvalid"
                  : null;
        setRejection(invalidReason);
        if (!invalidReason) onFileChange(selected);
    };

    return (
        <div className="field col-span-12">
            <label
                id="commission-document-upload-label"
                className={invalid ? "font-medium text-red-500" : "font-medium"}
            >
                {label}
                <span className="text-red-500 mx-1">*</span>
            </label>
            {hint}
            <div
                className="flex align-items-center gap-2 mt-3"
                role="group"
                aria-labelledby="commission-document-upload-label"
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    disabled={disabled}
                    onChange={(e) => {
                        chooseFile(e.target.files?.[0] ?? null);
                        e.target.value = "";
                    }}
                />
                <Button
                    type="button"
                    label={selectLabel}
                    icon="pi pi-upload"
                    className="shrink-0"
                    disabled={disabled}
                    onClick={() => fileInputRef.current?.click()}
                />
                {file && (
                    <>
                        <span className="text-sm text-muted-color min-w-0 truncate">{file.name}</span>
                        <Button
                            type="button"
                            icon="pi pi-times"
                            className="p-button-text p-button-sm p-button-rounded shrink-0"
                            disabled={disabled}
                            onClick={() => onFileChange(null)}
                        />
                    </>
                )}
            </div>
            {rejection && <p className="text-sm text-red-500 mt-2">{t(`commissionOrderDialog.${rejection}`)}</p>}
        </div>
    );
}

interface RegisterPanelProps {
    hasLegalInputs: boolean;
    registerData: RegisterData;
    onChange: (field: keyof RegisterData, value: string) => void;
    disabled: boolean;
    showInvalid: boolean;
}

export function RegisterPanel({hasLegalInputs, registerData, onChange, disabled, showInvalid}: RegisterPanelProps) {
    const {t} = useTranslation();
    if (!hasLegalInputs) return null;
    return (
        <div className="commission-panel-top">
            <span className="font-bold mb-3 block">{t("commissionOrderDialog.landRegister.title")}</span>
            <div className="formgrid commission-grid mt-4">
                {(
                    [
                        {f: "amtsgericht", k: "court"},
                        {f: "grundbuch", k: "register"},
                        {f: "gemarkung", k: "district"},
                        {f: "blattnummer", k: "sheet"},
                    ] as const
                ).map(({f, k}) => (
                    <div key={f} className="field col-span-6">
                        <label htmlFor={`docinfo-${f}`}>
                            {t(`commissionOrderDialog.landRegister.${k}`)}
                            <span className="commission-required">*</span>
                        </label>
                        <InputText
                            id={`docinfo-${f}`}
                            className={`w-full p-inputtext-sm${showInvalid && !registerData[f] ? " p-invalid" : ""}`}
                            value={registerData[f] ?? ""}
                            onChange={(e) => onChange(f, e.target.value)}
                            disabled={disabled}
                        />
                    </div>
                ))}
                <div className="field col-span-12">
                    <label htmlFor="docinfo-bemerkungen">{t("commissionOrderDialog.landRegister.remarks")}</label>
                    <InputTextarea
                        id="docinfo-bemerkungen"
                        className="w-full"
                        rows={3}
                        autoResize
                        value={registerData.bemerkungen ?? ""}
                        onChange={(e) => onChange("bemerkungen", e.target.value)}
                        disabled={disabled}
                    />
                </div>
            </div>
        </div>
    );
}

const CHECKED_RADIO_PT = {
    box: {style: {background: "#ffffff", borderColor: "var(--primary-color)"}},
    icon: {style: {backgroundColor: "var(--primary-color)", width: "12px", height: "12px"}},
};

interface ClientInfoPanelProps {
    legitimateInterest: boolean;
    onChange: (legitimateInterest: boolean) => void;
    creditorRegistered: boolean | null;
    onCreditorRegisteredChange: (creditorRegistered: boolean) => void;
    legalDocument: File | null;
    onLegalDocumentChange: (file: File | null) => void;
    disabled: boolean;
    showInvalid: boolean;
}

export function ClientInfoPanel({
    legitimateInterest,
    onChange,
    creditorRegistered,
    onCreditorRegisteredChange,
    legalDocument,
    onLegalDocumentChange,
    disabled,
    showInvalid,
}: ClientInfoPanelProps) {
    const {t} = useTranslation();
    const legitimateInterestInvalid = showInvalid && !legitimateInterest;
    const creditorRegisteredInvalid = showInvalid && creditorRegistered === null;
    return (
        <div className="commission-panel-top">
            <span className="font-bold">{t("commissionOrderDialog.client.title")}</span>
            <div className="flex align-items-start gap-2 mt-4">
                <Checkbox
                    inputId="client-legitimateInterest"
                    checked={!!legitimateInterest}
                    onChange={(e) => onChange(!!e.checked)}
                    disabled={disabled}
                />
                <div>
                    <label
                        htmlFor="client-legitimateInterest"
                        className={`cursor-pointer font-medium${legitimateInterestInvalid ? " text-red-500" : ""}`}
                    >
                        {t("commissionOrderDialog.client.legitimateInterest")}
                        <span className="commission-required">*</span>
                    </label>
                    <p className="text-sm text-color-secondary mt-1 mb-0">
                        {t("commissionOrderDialog.client.legitimateInterestHint")}
                    </p>
                </div>
            </div>
            <div className="field mt-4 mb-0">
                <label
                    id="commission-creditor-registered-label"
                    className={creditorRegisteredInvalid ? "font-medium text-red-500" : "font-medium"}
                >
                    {t("commissionOrderDialog.client.creditorRegistered")}
                    <span className="commission-required">*</span>
                </label>
                <div
                    className="flex gap-4 mt-2"
                    role="radiogroup"
                    aria-labelledby="commission-creditor-registered-label"
                >
                    <div className="flex align-items-center gap-2">
                        <RadioButton
                            inputId="client-creditorRegistered-true"
                            checked={creditorRegistered === true}
                            pt={creditorRegistered === true ? CHECKED_RADIO_PT : undefined}
                            onChange={() => onCreditorRegisteredChange(true)}
                            disabled={disabled}
                        />
                        <label htmlFor="client-creditorRegistered-true" className="cursor-pointer">
                            {t("commissionOrderDialog.client.creditorRegisteredYes")}
                        </label>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <RadioButton
                            inputId="client-creditorRegistered-false"
                            checked={creditorRegistered === false}
                            pt={creditorRegistered === false ? CHECKED_RADIO_PT : undefined}
                            onChange={() => onCreditorRegisteredChange(false)}
                            disabled={disabled}
                        />
                        <label htmlFor="client-creditorRegistered-false" className="cursor-pointer">
                            {t("commissionOrderDialog.client.creditorRegisteredNo")}
                        </label>
                    </div>
                </div>
            </div>
            {creditorRegistered !== null && (
                <div className="formgrid commission-grid mt-4">
                    <FileUploadField
                        key={String(creditorRegistered)}
                        creditorRegistered={creditorRegistered}
                        file={legalDocument}
                        onFileChange={onLegalDocumentChange}
                        disabled={disabled}
                        showInvalid={showInvalid}
                    />
                </div>
            )}
        </div>
    );
}

interface OwnerInfoPanelProps {
    ownerType: OwnerType | null;
    onOwnerTypeChange: (ownerType: OwnerType | null) => void;
    ownerData: OwnerData;
    onChange: (field: keyof OwnerData, value: string | null) => void;
    disabled: boolean;
    showInvalid: boolean;
}

export function OwnerInfoPanel({
    ownerType,
    onOwnerTypeChange,
    ownerData,
    onChange,
    disabled,
    showInvalid,
}: OwnerInfoPanelProps) {
    const {t, language} = useRevaTranslation();

    const ownerTypeOptions = [
        {label: t("commissionOrderDialog.owner.typeLegal"), value: "legal"},
        {label: t("commissionOrderDialog.owner.typeNatural"), value: "natural"},
    ];
    return (
        <div className="commission-panel-top">
            <span className="font-bold">{t("commissionOrderDialog.owner.title")}</span>
            <SelectButton
                className="mt-4 mb-4"
                value={ownerType}
                options={ownerTypeOptions}
                invalid={showInvalid && !ownerType}
                onChange={(e) => onOwnerTypeChange(e.value)}
                disabled={disabled}
            />
            <div className="formgrid commission-grid">
                {ownerType === "legal" && (
                    <div className="field col-span-12">
                        <label htmlFor="owner-legalEntity">
                            {t("commissionOrderDialog.owner.legalEntity")}
                            <span className="commission-required">*</span>
                        </label>
                        <InputText
                            id="owner-legalEntity"
                            className={`w-full p-inputtext-sm${showInvalid && !ownerData.ownerLegalEntity ? " p-invalid" : ""}`}
                            value={ownerData.ownerLegalEntity ?? ""}
                            onChange={(e) => onChange("ownerLegalEntity", e.target.value)}
                            disabled={disabled}
                        />
                    </div>
                )}
                {ownerType === "natural" && (
                    <>
                        <div className="field col-span-6">
                            <label htmlFor="owner-firstName">
                                {t("commissionOrderDialog.owner.firstName")}
                                <span className="commission-required">*</span>
                            </label>
                            <InputText
                                id="owner-firstName"
                                className={`w-full p-inputtext-sm${showInvalid && !ownerData.ownerFirstName ? " p-invalid" : ""}`}
                                value={ownerData.ownerFirstName ?? ""}
                                onChange={(e) => onChange("ownerFirstName", e.target.value)}
                                disabled={disabled}
                            />
                        </div>
                        <div className="field col-span-6">
                            <label htmlFor="owner-surname">
                                {t("commissionOrderDialog.owner.surname")}
                                <span className="commission-required">*</span>
                            </label>
                            <InputText
                                id="owner-surname"
                                className={`w-full p-inputtext-sm${showInvalid && !ownerData.ownerSurname ? " p-invalid" : ""}`}
                                value={ownerData.ownerSurname ?? ""}
                                onChange={(e) => onChange("ownerSurname", e.target.value)}
                                disabled={disabled}
                            />
                        </div>
                        <div className="field col-span-6">
                            <label htmlFor="owner-dob">
                                {t("commissionOrderDialog.owner.dateOfBirth")}
                                <span className="commission-required">*</span>
                            </label>
                            <Calendar
                                id="owner-dob"
                                className="w-full"
                                inputClassName={`p-inputtext-sm${showInvalid && !ownerData.ownerDateOfBirth ? " p-invalid" : ""}`}
                                value={ownerData.ownerDateOfBirth ? parseISO(ownerData.ownerDateOfBirth) : null}
                                onChange={(e) =>
                                    onChange(
                                        "ownerDateOfBirth",
                                        e.target.value ? format(e.target.value, "yyyy-MM-dd") : null,
                                    )
                                }
                                disabled={disabled}
                                showIcon
                                showButtonBar
                                dateFormat="dd.mm.yy"
                                locale={language}
                                pt={{dropdownButton: {root: {className: "p-button-sm"}}}}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
